import { Button, message } from "antd";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import useGetData from "../../api/useGetData";
import usePostData from "../../api/usePostData";
import { BlackLogo } from "../../assets";
import AppInput from "../../common/AppInput";
import AppPhoneInput from "../../common/AppPhoneInput";
import SelectInput from "../../common/SelectInput";
import FocusError from "../../utils/focusOnError";
import { updateProfileValidation } from "../../utils/validations";
import NationalitySelect from "./NationalitySelect";

const currentYear = dayjs().year();
const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
const months = Array.from({ length: 12 }, (_, i) => i + 1);
const days = Array.from({ length: 31 }, (_, i) => i + 1);

/**
 * The entry point to the user's profile page. It shows the user's info and enables them to update them.
 *
 * @returns {JSX.Element}
 */

export default function MyProfile() {
  const { t } = useTranslation("global");
  const { data: userInfoData, loading: gettingUserInfo } = useGetData({
    route: "api/method/airport_transport.api.user.get_user_info",
  });
  const { postData: updateUserInfo, loading: isUpdatingInfo } = usePostData();

  if (gettingUserInfo) {
    return <></>;
  }

  const onSubmit = async (values, { setSubmitting }) => {
    const dob = `${values.year}-${values.month}-${values.day}`;
    if (!dayjs(dob, "YYYY-M-D", true).isValid()) {
      message.error(t("errors.select_valid_date"));
      return;
    }
    const data = {
      first_name: values?.first_name,
      last_name: values?.last_name,
      nationality: values?.nationality,
      email: values?.email,
      mobile_number: values?.mobile_number,
      city: values?.city,
      state: values?.state,
      street: values?.street,
      dob,
    };
    const response = await updateUserInfo({
      route: "api/method/airport_transport.api.user.update_user_info",
      data,
    });
    if (response && response.status === 200) {
      const msg = response.data.msg;
      if (msg?.toLowerCase() === "transport user info updated successfully") {
        message.success(t("transport_user_info_updated"));
      } else {
        message.success(msg);
      }
    }
    setSubmitting(false);
  };

  return (
    <div>
      {userInfoData && (
        <main>
          <div className="container mx-auto p-4">
            <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-4">
              <Formik
                initialValues={{
                  first_name: userInfoData?.first_name,
                  last_name: userInfoData?.last_name,
                  day: userInfoData?.dob?.split("-")?.[2],
                  month: userInfoData?.dob?.split("-")?.[1],
                  year: userInfoData?.dob?.split("-")?.[0],
                  nationality: userInfoData?.nationality,
                  email: userInfoData?.email,
                  mobile_number: userInfoData?.mobile_number,
                  city: userInfoData?.city,
                  state: userInfoData?.state,
                  street: userInfoData?.street,
                }}
                validationSchema={updateProfileValidation}
                onSubmit={onSubmit}
              >
                {({ values, isSubmitting, setFieldValue, errors, touched }) => (
                  <Form className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <AppInput
                        label={t("my_profile.first_name_text")}
                        name="first_name"
                        type="text"
                        placeholder={t("my_profile.first_name_placeholder")}
                        error={errors.first_name}
                        touched={touched.first_name}
                      />
                      <AppInput
                        label={t("my_profile.last_name_text")}
                        placeholder={t("my_profile.last_name_placeholder")}
                        name="last_name"
                        type="text"
                        error={errors.last_name}
                        touched={touched.last_name}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="flex-1 flex flex-col gap-1">
                        <SelectInput
                          label={t("my_profile.day_text")}
                          name="day"
                          placeholder={t("select_day")}
                          onChange={(value) => setFieldValue("day", value)}
                          options={days.map((day) => ({
                            label: day,
                            value: day,
                          }))}
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-1">
                        <SelectInput
                          label={t("my_profile.month_text")}
                          placeholder={t("select_month")}
                          options={months.map((month) => ({
                            label: month,
                            value: month,
                          }))}
                          name={"month"}
                          onChange={(value) => setFieldValue("month", value)}
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-1">
                        <SelectInput
                          label={t("my_profile.year_text")}
                          onChange={(value) => setFieldValue("year", value)}
                          placeholder={t("select_year")}
                          options={years.map((year) => ({
                            value: year,
                            label: year,
                          }))}
                          name="year"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <NationalitySelect />
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <AppInput
                        label={t("my_profile.email_text")}
                        name="email"
                        type="email"
                        disabled={true}
                        error={errors.email}
                        touched={touched.email}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <AppPhoneInput
                        label={t("my_profile.phone_number_text")}
                        onChange={(val) => {
                          setFieldValue("mobile_number", val);
                        }}
                        value={values.mobile_number}
                        name="mobile_number"
                        disabled={true}
                        error={errors.mobile_number}
                        touched={touched.mobile_number}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <AppInput
                          label={t("my_profile.city_text")}
                          name="city"
                          type="text"
                          placeholder={t("my_profile.enter_city")}
                          error={errors.city}
                          touched={touched.city}
                        />
                      </div>
                      <div>
                        <AppInput
                          label={t("my_profile.state_text")}
                          name="state"
                          type="text"
                          placeholder={t("my_profile.enter_state")}
                          error={errors.state}
                          touched={touched.state}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <AppInput
                        label={t("my_profile.street_address_text")}
                        name="street"
                        type="text"
                        placeholder={t("my_profile.enter_street_address")}
                        error={errors.street}
                        touched={touched.street}
                      />
                    </div>
                    <Button
                      type="submit"
                      className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2"
                      disabled={isSubmitting}
                      htmlType="submit"
                      loading={isUpdatingInfo}
                    >
                      {t("my_profile.save_changes_text")}
                    </Button>
                    <FocusError />
                  </Form>
                )}
              </Formik>

              <div className="bg-background_steel_blue rounded-md flex items-center justify-center py-10 md:py-10 lg:py-0">
                <img
                  src={BlackLogo}
                  alt="Camelo Logo"
                  className="w-52 md:w-52 lg:w-22 h-64 md:h-52 lg:h-22"
                />
              </div>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}

const renderErrorMessage = (msg) => (
  <span
    style={{
      color: "#ff4d4f",
      fontSize: "small",
    }}
  >
    {msg}
  </span>
);
